import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import SvgIcon from "@mui/material/SvgIcon";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { DotsThree as DotsThreeIcon } from "@phosphor-icons/react/DotsThree";
import { Eye as EyeIcon } from "@phosphor-icons/react/Eye";
import { Lock as LockIcon } from "@phosphor-icons/react/Lock";
import { paths } from "config/paths";
import uniqBy from "lodash/uniqBy";
import Link from "next/link";
import * as React from "react";
import { displayBytes, getDatasetThumbnailUrl } from "utils";
import { useWebRouter } from "~utils/next-router/use-web-router";

import { usePopover } from "~ui-components/hooks/use-popover";
import { Dataset } from "~ui-components/types/__generated/gql/graphql";

import { DataTypeIcon } from "./data-type-icon";
import { ItemIcon } from "./item-icon";
import { ItemMenu } from "./item-menu";

export interface ItemRowProps {
	item: Dataset;
	onDelete?: (itemId: string) => void;
	isDeleteDataAllowed: boolean;
}

export function ItemRow({ item, onDelete, isDeleteDataAllowed }: ItemRowProps) {
	const popover = usePopover<HTMLButtonElement>();
	const router = useWebRouter();

	const handleDelete = React.useCallback(() => {
		popover.handleClose();
		onDelete?.(item.id);
	}, [item, popover, onDelete]);

	return (
		<React.Fragment>
			<TableRow
				key={item.id}
				sx={{
					bgcolor: "var(--mui-palette-background-paper)",
					borderRadius: 1.5,
					boxShadow: 0,
					transition: "box-shadow 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
					"&:hover": { boxShadow: "var(--mui-shadows-16)" },
					"& .MuiTableCell-root": {
						borderBottom: "1px solid var(--mui-palette-divider)",
						borderTop: "1px solid var(--mui-palette-divider)",
						"&:first-of-type": {
							borderTopLeftRadius: "12px",
							borderBottomLeftRadius: "12px",
							borderLeft: "1px solid var(--mui-palette-divider)",
						},
						"&:last-of-type": {
							borderTopRightRadius: "12px",
							borderBottomRightRadius: "12px",
							borderRight: "1px solid var(--mui-palette-divider)",
						},
					},
					cursor: "pointer",
				}}>
				<TableCell>
					<Stack
						component={Link}
						href={paths.accountName.data.datasetId.root(
							router._query.accountName,
							item.id,
						)}
						direction="row"
						spacing={2}
						sx={{
							alignItems: "center",

							color: "unset",
							textDecoration: "none",
						}}>
						<Box>
							<ItemIcon src={getDatasetThumbnailUrl(item.id, item.updatedAt)} />
						</Box>
						<Box sx={{ minWidth: 0 }}>
							<Typography
								noWrap
								variant="subtitle2">
								{item.name}
							</Typography>
							<Typography
								color="text.secondary"
								variant="caption">
								{displayBytes(item.size)} • Used in{" "}
								{getDatasetMaps(item).length} maps
							</Typography>
						</Box>
					</Stack>
				</TableCell>
				<TableCell
					align="right"
					sx={{ maxWidth: "25px" }}>
					<SvgIcon>{item?.isPublic ? <EyeIcon /> : <LockIcon />}</SvgIcon>
				</TableCell>
				<TableCell
					align="right"
					sx={{ maxWidth: "25px" }}>
					<SvgIcon>{DataTypeIcon[item.type]}</SvgIcon>
				</TableCell>
				{isDeleteDataAllowed && (
					<TableCell
						align="right"
						sx={{ maxWidth: "25px" }}>
						<IconButton
							onClick={popover.handleOpen}
							ref={popover.anchorRef}>
							<DotsThreeIcon weight="bold" />
						</IconButton>
					</TableCell>
				)}
			</TableRow>
			{isDeleteDataAllowed && (
				<ItemMenu
					anchorEl={popover.anchorRef.current ?? undefined}
					onClose={popover.handleClose}
					onDelete={handleDelete}
					open={popover.open}
				/>
			)}
		</React.Fragment>
	);
}

export const getDatasetMaps = (item: Dataset) =>
	uniqBy(
		item._layers
			.map((l) => l._layerGroup)
			.flat()
			.map((lg) => lg._map)
			.flat(),
		"id",
	);
